import {
  And,
  Constant,
  EraQL,
  Expression,
  If,
  MakeArray,
  MakeStruct,
  Relation,
  Ty,
} from '@cotera/era';
import {
  Apply,
  ColFailure,
  ColMetadata,
  ColResult,
  ColSuccess,
  COMPARE_FUNCS,
  GraphError,
  Identity,
  InterNodeLocation,
  IToolResolver,
  LlmColConfigV2,
  RelNode,
  ResolutionOrder,
} from './types';
import { Assert } from '@cotera/utilities';
import { err, ok, Result } from 'neverthrow';
import { mkEraqlExprChecker, ParseCtxFor } from './eraql-expr-checker';
import _ from 'lodash';
import { EventStreamDefinition } from '../../manifest';
import { EventStream } from '../../builder';
import { applyRelColsInOrder } from './apply-rel-cols-in-order';
import { buildTypecheckingSpecialForms } from '../../workflow/build-typechecking-special-forms';
import { DocumentEditor } from '../document';
import { BuildExplorer } from '../commit/build-explorer';
import { LLMMessage } from '@cotera/api';

export const resolveRelCols = (params: {
  rel: Relation;
  relIdent: Identity;
  cols: RelNode['cols'];
  pkRes: Expression | null;
  resolutionOrder: ResolutionOrder;
  ctxFor: ParseCtxFor;
  toolResolver: IToolResolver;
}): Result<
  Record<string, ColSuccess>,
  { err: GraphError; attributes: Record<string, Ty.ExtendedAttributeType> }
> => {
  const { relIdent, cols, ctxFor, pkRes, resolutionOrder, rel, toolResolver } =
    params;
  const checkExpr = mkEraqlExprChecker(ctxFor);
  const colResolutions: Record<string, ColResult> = {};

  const resolveCol = (name: string, attrStack: string[]): ColResult | null => {
    if (Object.hasOwn(rel.attributes, name)) {
      const e: GraphError = { t: 'aliasing-error', name };
      return err(['source', e]);
    }

    const existing = colResolutions[name];

    if (existing) {
      return existing;
    }

    const attrDeps: Record<string, EraQL.TokenMeta[]> = {};
    const col = cols[name];

    if (col === undefined) {
      return null;
    }

    const { def } = col;

    let apply: Apply;
    let meta: ColMetadata;
    let eventStream: EventStreamDefinition | null = null;

    const attributes: EraQL.AttrLookup = (attrDepName, meta) => {
      (attrDeps[attrDepName] ??= []).push(meta);
      const inRel = rel.attributes[attrDepName];

      if (inRel) {
        return ok(
          Expression.fromAst({
            t: 'attr',
            name: attrDepName,
            source: 'from',
            ty: inRel,
          })
        );
      }

      const innerResolution = resolveCol(attrDepName, [
        attrDepName,
        ...attrStack,
      ]);

      if (innerResolution === null) {
        return null;
      }

      colResolutions[attrDepName] ??= innerResolution;

      if (innerResolution.isOk()) {
        return ok(
          Expression.fromAst({
            t: 'attr',
            name: attrDepName,
            source: 'from',
            ty: tyFromApply(innerResolution.value.apply),
          })
        );
      } else {
        return err([
          { t: 'custom', msg: `Attribute "${attrDepName}" has an error` },
        ]);
      }
    };

    switch (def.t) {
      case 'eraql': {
        const parseRes = checkExpr(['col', name, 'source'], def.source, {
          attributes,
          disallowAggregate: true,
          disallowWindow: false,
        });

        if (parseRes.isOk()) {
          apply = { t: 'expr', expr: parseRes.value };
          meta = { t: 'eraql', expr: parseRes.value };
          break;
        } else {
          const e: ColFailure = [
            'source',
            {
              t: 'multi-error',
              errs: parseRes.error,
            },
          ];
          return err(e);
        }
      }
      case 'llm': {
        // Convert v1 to v2 format first, then use v2 path everywhere
        let v2Config: LlmColConfigV2;

        if (def.version === 'v2') {
          v2Config = def;
        } else {
          // Convert v1 to v2
          const v1Config = def;

          // Create EditorState from v1 config
          const editorState = DocumentEditor.EditorState.fromV1({
            columnName: name,
            llmConfig: v1Config,
          });

          // Validate outputTy before conversion
          const outputTyParsed = EraQL.parseTy(v1Config.outputTy);
          if (outputTyParsed.isErr()) {
            const e: ColFailure = [
              'outputTy',
              {
                t: 'eraql-errors',
                errs: outputTyParsed.error,
                source: v1Config.outputTy,
              },
            ];
            return err(e);
          }

          // Convert to v2 format
          v2Config = {
            t: 'llm',
            version: 'v2',
            outputTy: v1Config.outputTy,
            editorState: editorState.getState(),
            model: v1Config.model,
            schedule: v1Config.when,
            temperature: v1Config.temperature,
            topP: v1Config.topP,
            topK: v1Config.topK,
            frequencyPenalty: v1Config.frequencyPenalty,
            presencePenalty: v1Config.presencePenalty,
            maxOutputTokens: v1Config.maxOutputTokens,
          };
        }

        const when = v2Config.schedule ?? 'never';
        const config = new DocumentEditor.EditorState(v2Config.editorState);

        // Extract ALL dependencies (mentions, expressions, attachments) from the editor state
        // getDependencies() returns symbol references which could be:
        // 1. Top-level symbols (documents, agents, tools) - handled by top-level resolution
        // 2. Column names - need to be registered as column dependencies for proper ordering
        const allDependencies = config.getDependencies(BuildExplorer.empty());

        for (const depName of allDependencies) {
          // Check if this dependency is a column in this relation
          // Column names are local to the relation, not top-level symbols
          if (Object.hasOwn(cols, depName)) {
            // This is a column dependency - resolve it first to ensure proper ordering
            const depRes = resolveCol(depName, [...attrStack, name]);

            if (depRes === null) {
              // Column doesn't exist (shouldn't happen since we checked Object.hasOwn)
              const e: ColFailure = [
                'prompt',
                {
                  t: 'multi-error',
                  errs: [
                    [
                      ['col', depName, 'source'] as const,
                      { t: 'aliasing-error', name: depName },
                    ],
                  ],
                },
              ];
              return err(e);
            }

            if (depRes.isErr()) {
              // Column has errors - propagate the error
              return depRes;
            }

            // Register as dependency so applyRelColsInOrder knows about it
            (attrDeps[depName] ??= []).push({
              range: [0, 0] as const,
              comments: { leading: [], trailing: null },
            });
          }
          // Note: Non-column dependencies (documents, agents, tools) are handled
          // by the top-level resolution in resolve.ts and don't need to be
          // registered in attrDeps
        }

        const rawMessages = config.getMessages(
          BuildExplorer.empty(),
          'expression'
        );

        const messageRes = convertModelMessagesToExpression(
          name,
          rawMessages,
          ctxFor,
          attributes
        );

        if (messageRes.isErr()) {
          const e: ColFailure = [
            'prompt',
            { t: 'multi-error', errs: messageRes.error },
          ];
          return err(e);
        }

        const messages = messageRes.value;

        const ty = config.getOutputTy();
        const model = v2Config.model;

        // Extract and validate tool mentions using the tool resolver
        const toolMentions = config
          .getMentions(BuildExplorer.empty())
          .filter((x) => x.type === 'tool')
          .map((x) => x.referenceId ?? x.id);

        // Validate that all mentioned tools exist
        for (const toolKey of toolMentions) {
          if (!toolResolver.isToolAvailable(toolKey)) {
            const e: ColFailure = [
              'tools',
              { t: 'ffi-tool-not-found', type: toolKey, key: toolKey },
            ];
            return err(e);
          }
        }

        const tools = toolMentions;
        const temperature = v2Config.temperature;
        const topP = v2Config.topP;
        const topK = v2Config.topK;
        const frequencyPenalty = v2Config.frequencyPenalty;
        const presencePenalty = v2Config.presencePenalty;
        const maxOutputTokens = v2Config.maxOutputTokens;

        Assert.assert(messageRes !== null && messageRes.isOk());

        const identifier: Expression | null = pkRes;

        meta = {
          t: 'llm',
          prompt: messageRes.value,
        };

        const uddWriteKey = col.meta.identity.toString();

        if (when !== 'never' && identifier === null) {
          const e: ColFailure = ['when', { t: 'requires-pk' }];
          return err(e);
        }

        if (identifier === null) {
          apply = {
            t: 'expr',
            expr: Constant(null, { ty: Ty.makeNullable(ty) }),
          };
        } else {
          apply = {
            t: 'udd',
            key: uddWriteKey,
            identifier,
            ty,
          };
        }

        if (identifier) {
          const colSuccesses: Record<string, ColSuccess> = Object.fromEntries(
            Object.entries(colResolutions).flatMap(([name, res]) =>
              res.map((x): [string, ColSuccess][] => [[name, x]]).unwrapOr([])
            )
          );

          eventStream = EventStream({
            specifier: {
              t: 'graph',
              colIdentity: col.meta.identity.asTuple(),
              relIdentity: relIdent.asTuple(),
            },
            rel: applyRelColsInOrder({
              relIdent,
              base: rel,
              cols: colSuccesses,
            })
              .select(() => {
                return {
                  id: identifier.andThen((t) =>
                    t.implements('string') ? t : t.cast('string')
                  ),
                  nullOr1: If(checkMessageNullity(rawMessages, messages), {
                    then: Constant(1, { ty: 'int' }),
                    else: Constant(null, { ty: 'int' }),
                  }),
                  messages: messages.cast(Ty.ty('super')),
                };
              })
              .where((t) => t.attr('nullOr1').eq(1)),
            identifier: (t) => t.attr('id'),
            onChange: (t) => {
              return t.attr('nullOr1');
            },
            when,
            sinks: {
              default: {
                config: {
                  t: 'llmCompletion',
                  config: {
                    model: model,
                    tools: tools,
                    temperature: temperature,
                    topP: topP,
                    topK: topK,
                    frequencyPenalty: frequencyPenalty,
                    presencePenalty: presencePenalty,
                    uddWriteKey,
                    identity: {
                      rel: relIdent.toString(),
                      col: col.meta.identity.toString(),
                    },
                    maxOutputTokens: maxOutputTokens,
                    durable: true,
                    expectedType: ty,
                    messages: [],
                  },
                },
                condition: () => true,
                select: (t) => t.pick('id', 'messages'),
              },
            },
          });
        }

        break;
      }

      case 'workflow': {
        const { source, toolSymbolMap } = def;

        // Validate that all tools in the toolSymbolMap exist
        for (const toolKey of Object.values(toolSymbolMap)) {
          if (!toolResolver.isToolAvailable(toolKey.toolId)) {
            const e: ColFailure = [
              'toolSymbolMap',
              {
                t: 'ffi-tool-not-found',
                type: toolKey.toolName,
                key: toolKey.toolId,
              },
            ];
            return err(e);
          }
        }

        const bindings = buildTypecheckingSpecialForms({
          tools: toolSymbolMap,
          toolResolver,
        });

        const sourceExprRes = checkExpr(['col', name, 'source'], source, {
          attributes,
          bindings,
          disallowAggregate: true,
          disallowWindow: true,
        });

        if (sourceExprRes.isErr()) {
          const error: ColFailure = [
            'source',
            { t: 'multi-error', errs: sourceExprRes.error },
          ];
          return err(error);
        }

        const sourceTy = sourceExprRes.value.ty;

        const identifier: Expression | null = pkRes;

        const uddWriteKey = col.meta.identity.toString();

        if (def.when !== 'never' && identifier === null) {
          const e: ColFailure = ['when', { t: 'requires-pk' }];
          return err(e);
        }

        if (identifier === null) {
          apply = {
            t: 'expr',
            expr: Constant(null, { ty: Ty.makeNullable(sourceTy) }),
          };
        } else {
          apply = {
            t: 'udd',
            key: uddWriteKey,
            identifier,
            ty: sourceTy,
          };
        }

        meta = { t: 'workflow' };

        if (identifier) {
          const colSuccesses: Record<string, ColSuccess> = Object.fromEntries(
            Object.entries(colResolutions).flatMap(([name, res]) =>
              res.map((x): [string, ColSuccess][] => [[name, x]]).unwrapOr([])
            )
          );

          const deps = Object.keys(attrDeps);

          eventStream = EventStream({
            specifier: {
              t: 'graph',
              colIdentity: col.meta.identity.asTuple(),
              relIdentity: relIdent.asTuple(),
            },
            rel: applyRelColsInOrder({
              relIdent,
              base: rel,
              cols: colSuccesses,
            }).select((t) => ({
              id: identifier.andThen((t) =>
                t.implements('string') ? t : t.cast('string')
              ),
              input: MakeStruct(
                Object.fromEntries(deps.map((dep) => [dep, t.attr(dep)]))
              ),
              trigger: If(And(...deps.map((dep) => t.attr(dep).isNotNull())), {
                then: 1,
              }),
            })),
            identifier: (t) => t.attr('id'),
            onChange: (t) => t.attr('trigger'),
            when: def.when,
            sinks: {
              default: {
                config: {
                  t: 'workflowProgram',
                  config: {
                    program: source,
                    toolSymbolMap,
                    relIdentity: relIdent.asTuple(),
                    colIdentity: col.meta.identity.asTuple(),
                    partialUddWriteKey: {
                      actorId: undefined, // TODO later
                      entityId: relIdent.toString(),
                      key: uddWriteKey,
                    },
                  },
                },
                condition: () => true,
                select: (t) => t.pick('id', 'input'),
              },
            },
          });
        }

        break;
      }
      default:
        return Assert.unreachable(def);
    }

    const res: ColResult = ok({
      apply,
      deps: attrDeps,
      eventStream,
      identity: col.meta.identity,
      meta,
    });
    return res;
  };

  Object.keys(cols)
    .sort((a, b) => COMPARE_FUNCS[resolutionOrder](a, b))
    .forEach((name) => {
      const result = resolveCol(name, [])!;
      colResolutions[name] ??= result;
    });

  const successes: Record<string, ColSuccess> = {};
  const errs: [location: InterNodeLocation, err: GraphError][] = [];

  for (const [colName, res] of Object.entries(colResolutions)) {
    if (res.isErr()) {
      const [loc, err] = res.error;
      errs.push([['col', colName, loc], err]);
    } else {
      successes[colName] = res.value;
    }
  }

  if (errs.length > 0) {
    const e: GraphError = {
      t: 'multi-error',
      errs,
    };
    return err({
      err: e,
      attributes: _.mapValues(successes, (x) =>
        x.apply.t === 'expr' ? x.apply.expr.ty : x.apply.ty
      ),
    });
  }

  return ok(successes);
};

// Helper function to get the content field from a message expression based on role
const getMessageContentField = (
  messageExpr: Expression,
  role: LLMMessage['role']
): Expression => {
  switch (role) {
    case 'system':
    case 'user':
    case 'assistant':
      return messageExpr.getField('content');
    case 'file':
      return messageExpr.getField('data');
    case 'image':
      return messageExpr.getField('images');
    default:
      return Assert.unreachable(role);
  }
};

//the messages contain eraql expressions, so we need to convert them to expressions
const convertModelMessagesToExpression = (
  name: string,
  messages: LLMMessage[],
  ctxFor: ParseCtxFor,
  attributes: EraQL.AttrLookup
): Result<Expression, [loc: InterNodeLocation, err: GraphError][]> => {
  const checkExpr = mkEraqlExprChecker(ctxFor);
  const convertedMessages = messages.map((message) => {
    if (message.role === 'system') {
      const messageRes = checkExpr(['col', name, 'prompt'], message.content, {
        attributes,
        disallowAggregate: true,
        disallowWindow: true,
      });

      if (messageRes.isOk()) {
        return ok(
          MakeStruct({
            role: message.role,
            content: messageRes.value,
          })
        );
      }

      return messageRes;
    }

    if (message.role === 'user' || message.role === 'assistant') {
      const messageRes = checkExpr(['col', name, 'prompt'], message.content, {
        attributes,
        disallowAggregate: true,
        disallowWindow: true,
      });

      if (messageRes.isOk()) {
        return ok(
          MakeStruct({
            role: message.role,
            content: messageRes.value,
          })
        );
      }

      return messageRes;
    }
    if (message.role === 'file') {
      const messageRes = checkExpr(['col', name, 'prompt'], message.data, {
        attributes,
        disallowAggregate: true,
        disallowWindow: true,
      });

      if (messageRes.isOk()) {
        return ok(
          MakeStruct({
            role: message.role,
            filename:
              message.filename ?? Constant(null, { ty: Ty.ty('string') }),
            data: messageRes.value,
          })
        );
      }

      return messageRes;
    }

    if (message.role === 'image') {
      // Convert each image URL to an expression
      const imageResults: Result<
        Expression,
        [loc: InterNodeLocation, err: GraphError][]
      >[] = message.images.map((image) => {
        const imageRes = checkExpr(['col', name, 'prompt'], image.url, {
          attributes,
          disallowAggregate: true,
          disallowWindow: true,
        });

        if (imageRes.isOk()) {
          return ok(
            MakeStruct({
              url: imageRes.value,
            })
          );
        }

        return imageRes;
      });

      // Combine all image results
      const combinedImages = Result.combineWithAllErrors(imageResults);

      if (combinedImages.isOk()) {
        return ok(
          MakeStruct({
            role: message.role,
            images: MakeArray(combinedImages.value as Expression[]),
          })
        );
      }

      return combinedImages.mapErr(
        (errors): [loc: InterNodeLocation, err: GraphError][] =>
          errors.flat(2) as unknown as [
            loc: InterNodeLocation,
            err: GraphError
          ][]
      );
    }

    return Assert.unreachable(message);
  }) as Result<Expression, [loc: InterNodeLocation, err: GraphError][]>[];

  return Result.combineWithAllErrors(convertedMessages)
    .map((expressions) => {
      // Check if all message contents are not null
      const allContentsNotNull = messages.reduce((acc, rawMessage, index) => {
        const messageExpr = expressions[index];
        if (!messageExpr) {
          return Constant(false);
        }
        return And(
          acc,
          getMessageContentField(messageExpr, rawMessage.role).isNotNull()
        );
      }, Constant(true));

      const messagesArray = MakeArray(
        expressions.map((expr) => expr.cast(Ty.ty('super')))
      );
      return If(allContentsNotNull, {
        then: messagesArray.cast(
          Ty.ty(messagesArray.ty, {
            nullable: true,
          })
        ),
      });
    })
    .mapErr(
      (errors): [loc: InterNodeLocation, err: GraphError][] =>
        errors.flat(2) as unknown as [loc: InterNodeLocation, err: GraphError][]
    );
};

const checkMessageNullity = (
  rawMessages: LLMMessage[],
  messagesExpr: Expression
): Expression => {
  if (rawMessages.length === 0) {
    return Constant(false);
  }

  // messagesExpr is the result of convertModelMessagesToExpression, which already
  // checks all message contents and returns null if any content is null.
  // So if messagesExpr is not null, all contents are verified to be not null.
  return messagesExpr.isNotNull();
};

const tyFromApply = (apply: Apply): Ty.ExtendedAttributeType =>
  apply.t === 'expr' ? apply.expr.ty : apply.ty;
